import { useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { FilterContextData } from 'contexts/FilterContext';
import { isPpaPerfSummaryScreen, PPA_HOME_ROUTE, PPA_PACK_ARCHITECTURE_SCREEN } from 'config/urls';
import { usePpaDefaultTimePeriod } from 'hooks/ppa/usePpaDefaultTimePeriod';

function parseTimePeriodSelection(timePeriodSelection?: string) {
    try {
        return JSON.parse(timePeriodSelection ?? '{}');
    } catch {
        return {};
    }
}

function hasValidTimestamps(start?: number, end?: number) {
    return (
        Number.isFinite(start) &&
        Number.isFinite(end) &&
        (start ?? 0) > 0 &&
        (end ?? 0) > 0
    );
}

/**
 * Resolves PPA query filters and time period.
 * Performance Summary uses inherited scope until the user applies global filters.
 */
export function usePpaQueryFilters() {
    const { pathname } = useLocation();
    const screenName = pathname.slice(pathname.lastIndexOf('/') + 1);
    const isPpaHomeRoute = pathname.includes(PPA_HOME_ROUTE);
    const isPerfSummaryRoute = isPpaPerfSummaryScreen(screenName);
    const isPackArchitectureRoute = screenName === PPA_PACK_ARCHITECTURE_SCREEN;

    const {
        ppaScopeFilterIds,
        ppaScopeFilterLoading,
        idForRestOfComponent,
        idForRestOfComponentLoading,
        timePeriodSelection,
        timeSelectionLabel,
        ppaGlobalFilterApplied,
        filterNullError,
    } = useContext(FilterContextData);

    const {
        startTimestamp: defaultStartTimestamp,
        endTimestamp: defaultEndTimestamp,
        timePeriodLabel: defaultTimePeriodLabel,
        hasValidTimePeriod: hasDefaultTimePeriod,
        isLoading: defaultTimePeriodLoading,
    } = usePpaDefaultTimePeriod();

    const useGlobalFilterForQueries = isPackArchitectureRoute || (isPerfSummaryRoute && ppaGlobalFilterApplied);

    const scopeDim1Ids = ppaScopeFilterIds?.dim1?.ids ?? [];
    const scopeDim2Ids = ppaScopeFilterIds?.dim2?.ids ?? [];
    const globalDim1Ids = idForRestOfComponent?.dim1?.ids ?? [];
    const globalDim2Ids = idForRestOfComponent?.dim2?.ids ?? [];

    const dim1_id_list = useGlobalFilterForQueries ? globalDim1Ids : scopeDim1Ids;
    const dim2_id_list = useGlobalFilterForQueries ? globalDim2Ids : scopeDim2Ids;
    const hasFilterIds = dim1_id_list.length > 0 || dim2_id_list.length > 0;
    const hasResolvedFilterIds = isPackArchitectureRoute
        ? !idForRestOfComponentLoading && hasFilterIds
        : hasFilterIds || (useGlobalFilterForQueries && !idForRestOfComponentLoading);

    const parsedGlobalTimePeriod = useMemo(
        () => parseTimePeriodSelection(timePeriodSelection),
        [timePeriodSelection],
    );
    const globalStartTimestamp = parsedGlobalTimePeriod?.start_timestamp;
    const globalEndTimestamp = parsedGlobalTimePeriod?.end_timestamp;
    const hasGlobalTimePeriod = hasValidTimestamps(globalStartTimestamp, globalEndTimestamp);

    const startTimestamp = useGlobalFilterForQueries && hasGlobalTimePeriod
        ? globalStartTimestamp
        : defaultStartTimestamp;
    const endTimestamp = useGlobalFilterForQueries && hasGlobalTimePeriod
        ? globalEndTimestamp
        : defaultEndTimestamp;
    const hasValidTimePeriod = useGlobalFilterForQueries
        ? hasGlobalTimePeriod || hasDefaultTimePeriod
        : hasDefaultTimePeriod;

    const filtersLoading = useGlobalFilterForQueries
        ? ppaScopeFilterLoading || idForRestOfComponentLoading
        : ppaScopeFilterLoading;
    const globalFilterQueryKey = useMemo(
        () =>
            JSON.stringify({
                mode: useGlobalFilterForQueries ? 'applied' : 'scope',
                applied: ppaGlobalFilterApplied,
                dim1: dim1_id_list,
                dim2: dim2_id_list,
                startTimestamp,
                endTimestamp,
            }),
        [
            dim1_id_list,
            dim2_id_list,
            endTimestamp,
            ppaGlobalFilterApplied,
            startTimestamp,
            useGlobalFilterForQueries,
        ],
    );

    const timePeriodLabel = useGlobalFilterForQueries && timeSelectionLabel
        ? timeSelectionLabel
        : defaultTimePeriodLabel;

    return {
        isPpaHomeRoute,
        isPerfSummaryRoute,
        useGlobalFilterForQueries,
        globalFilterQueryKey,
        dim1_id_list,
        dim2_id_list,
        hasResolvedFilterIds,
        startTimestamp,
        endTimestamp,
        timePeriodLabel,
        hasValidTimePeriod,
        filtersLoading,
        defaultTimePeriodLoading,
        filterNullError,
    };
}
